import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap';
import { getConfig } from '../../../config';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import { FulfilmentPicClient } from '../../../infrastructure/FulfilmentPicClient';
import {
  FulfilmentPicPurpose,
  FulfilmentPicRoutingMode,
  FulfilmentPicWeekday,
  resolvePicPurposes,
  type FulfilmentPicConfig,
  type FulfilmentPicProfile,
  type GigworkAccountLimitMode,
} from '@/models/FulfilmentPicConfig';
import {
  buildPicRosterCoverage,
  msUntilNextSgtMidnight,
  picDutyStatusLabels,
  picPurposeDescriptions,
  picPurposeLabels,
  PicDutyStatus,
  type PicPurposeCoverage,
} from './picPurposeCoverage';

const weekdays = Object.values(FulfilmentPicWeekday);
const purposes = Object.values(FulfilmentPicPurpose);
const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;
const brandSlugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
interface ParsedWeekendAssignments {
  assignments: string[];
  invalid: string[];
}

interface ParsedBrandSlugs {
  slugs: string[];
  invalid: string[];
  duplicates: string[];
}

const isIsoDate = (value: string): boolean => {
  if (!isoDatePattern.test(value)) {
    return false;
  }
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

const parseWeekendAssignments = (value: string): ParsedWeekendAssignments => {
  const entries = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  return {
    assignments: entries.filter(isIsoDate),
    invalid: entries.filter((item) => !isIsoDate(item)),
  };
};

const parseBrandSlugs = (value: string): ParsedBrandSlugs => {
  const entries = value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  entries.forEach((slug) => {
    if (seen.has(slug)) {
      duplicates.add(slug);
    }
    seen.add(slug);
  });
  return {
    slugs: entries,
    invalid: entries.filter((slug) => !brandSlugPattern.test(slug)),
    duplicates: [...duplicates],
  };
};

// Keeps enum order so the legacy single purpose (purposes[0]) is the first purchase purpose a PIC holds.
const togglePurpose = (current: FulfilmentPicPurpose[], purpose: FulfilmentPicPurpose): FulfilmentPicPurpose[] =>
  purposes.filter((item) => (item === purpose ? !current.includes(item) : current.includes(item)));

const describeRouting = (group: PicPurposeCoverage): string => {
  if (group.purpose === null) {
    return "Weighed for an order only when nobody holding that order's purpose is on duty.";
  }
  if (group.members.length === 0) {
    if (group.purpose === FulfilmentPicPurpose.BULK_PURCHASE) {
      return 'No bulk PIC set: bulk orders use the Facade default.';
    }
    if (group.purpose === FulfilmentPicPurpose.GIG_HANDBACK) {
      return 'No hand-back PIC designated: hand-backs follow the Coupang purchase rule.';
    }
    return 'No PIC designated: the rule is ignored and orders are assigned by weight among every on-duty PIC.';
  }
  return group.hasMemberOnDuty
    ? 'Assigned by weight among the designated PICs on duty.'
    : 'No designated PIC on duty today: the rule is ignored and orders are assigned by weight among every on-duty PIC.';
};

const normalizeConfig = (config: FulfilmentPicConfig): FulfilmentPicConfig => ({
  ...config,
  gigworkExcludedBrandSlugs: config.gigworkExcludedBrandSlugs ?? [],
  gigworkDailyAccountOrderLimit: config.gigworkDailyAccountOrderLimit ?? 30,
  gigworkAccountLimitMode: config.gigworkAccountLimitMode === 'enforce' ? 'enforce' : 'observe-only',
});

const createWeekendDrafts = (config: FulfilmentPicConfig): Record<string, string> =>
  Object.fromEntries(config.pics.map((pic) => [pic.picId, pic.weekendAssignments.join(', ')]));

const createPic = (): FulfilmentPicProfile => ({
  picId: window.crypto.randomUUID(),
  name: '',
  slackId: '',
  activeDays: [
    FulfilmentPicWeekday.MON,
    FulfilmentPicWeekday.TUE,
    FulfilmentPicWeekday.WED,
    FulfilmentPicWeekday.THU,
    FulfilmentPicWeekday.FRI,
  ],
  isActive: true,
  leaveStart: null,
  leaveEnd: null,
  weight: 1,
  weekendAssignments: [],
});

const emptyConfig: FulfilmentPicConfig = {
  routingMode: FulfilmentPicRoutingMode.N8N,
  gigworkAllocationPercentage: 0,
  gigworkRewardKrw: 1000,
  gigworkExcludedBrandSlugs: [],
  pics: [],
};

export default function FulfilmentPicManagementPage() {
  const client = useMemo(() => new FulfilmentPicClient(getAdminApiUrl(AdminApiDomain.ORDER), getConfig().apiKey), []);
  const [config, setConfig] = useState<FulfilmentPicConfig>(emptyConfig);
  const [excludedBrandDraft, setExcludedBrandDraft] = useState('');
  const [accountLimitDraft, setAccountLimitDraft] = useState('30');
  const [loadedAccountPolicy, setLoadedAccountPolicy] = useState({
    limit: 30,
    mode: 'observe-only' as GigworkAccountLimitMode,
  });
  const [weekendDrafts, setWeekendDrafts] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedConfig, setHasLoadedConfig] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [coverageAt, setCoverageAt] = useState(() => new Date());

  const loadConfig = useCallback(async () => {
    setIsLoading(true);
    setHasLoadedConfig(false);
    setError(null);
    try {
      const result = await client.getConfig();
      const loadedConfig = normalizeConfig(result.config);
      setConfig(loadedConfig);
      setAccountLimitDraft(String(loadedConfig.gigworkDailyAccountOrderLimit));
      setLoadedAccountPolicy({
        limit: loadedConfig.gigworkDailyAccountOrderLimit ?? 30,
        mode: loadedConfig.gigworkAccountLimitMode ?? 'observe-only',
      });
      setExcludedBrandDraft(loadedConfig.gigworkExcludedBrandSlugs.join(', '));
      setWeekendDrafts(createWeekendDrafts(loadedConfig));
      setHasLoadedConfig(true);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load PIC config');
    } finally {
      setIsLoading(false);
    }
  }, [client]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  // Keep "today" current for a page left open across SGT midnight or in a background tab.
  useEffect(() => {
    const refresh = () => setCoverageAt(new Date());
    const midnightTimer = window.setTimeout(refresh, msUntilNextSgtMidnight(coverageAt));
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.clearTimeout(midnightTimer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [coverageAt]);

  const updatePic = (picId: string, update: Partial<FulfilmentPicProfile>) => {
    setConfig((current) => ({
      ...current,
      pics: current.pics.map((pic) => (pic.picId === picId ? { ...pic, ...update } : pic)),
    }));
  };

  const toggleActiveDay = (pic: FulfilmentPicProfile, day: FulfilmentPicWeekday) => {
    updatePic(pic.picId, {
      activeDays: pic.activeDays.includes(day)
        ? pic.activeDays.filter((activeDay) => activeDay !== day)
        : [...pic.activeDays, day],
    });
  };

  const saveConfig = async () => {
    if (!hasLoadedConfig) {
      return;
    }

    if (!Number.isInteger(config.gigworkAllocationPercentage)) {
      setSuccess(null);
      setError('GIGWORK allocation percentage must be an integer.');
      return;
    }

    if (!Number.isInteger(config.gigworkRewardKrw)) {
      setSuccess(null);
      setError('GIGWORK reward must be an integer.');
      return;
    }

    const accountLimit = Number(accountLimitDraft);
    if (!Number.isSafeInteger(accountLimit) || accountLimit <= 0) {
      setSuccess(null);
      setError('Coupang account daily limit must be a positive integer.');
      return;
    }

    const parsedBrandSlugs = parseBrandSlugs(excludedBrandDraft);
    if (parsedBrandSlugs.invalid.length > 0) {
      setSuccess(null);
      setError(`Excluded brands must use lowercase WooCommerce slugs: ${parsedBrandSlugs.invalid.join(', ')}.`);
      return;
    }
    if (parsedBrandSlugs.duplicates.length > 0) {
      setSuccess(null);
      setError(`Excluded brand slugs must be unique: ${parsedBrandSlugs.duplicates.join(', ')}.`);
      return;
    }

    const invalidWeightPic = config.pics.find((pic) => !Number.isInteger(pic.weight));
    if (invalidWeightPic) {
      setSuccess(null);
      setError(`PIC weight must be an integer for ${invalidWeightPic.name || 'the selected PIC'}.`);
      return;
    }

    const invalidWeekendPic = config.pics.find((pic) => {
      const draft = weekendDrafts[pic.picId] ?? pic.weekendAssignments.join(', ');
      return parseWeekendAssignments(draft).invalid.length > 0;
    });
    if (invalidWeekendPic) {
      setSuccess(null);
      setError(`Weekend dates must use YYYY-MM-DD for ${invalidWeekendPic.name || 'the selected PIC'}.`);
      return;
    }

    const missingLeaveStartPic = config.pics.find((pic) => pic.leaveEnd && !pic.leaveStart);
    if (missingLeaveStartPic) {
      setSuccess(null);
      setError(`Leave start is required when leave end is set for ${missingLeaveStartPic.name || 'the selected PIC'}.`);
      return;
    }

    const invalidLeaveRange = config.pics.find(
      (pic) => pic.leaveStart && pic.leaveEnd && pic.leaveEnd < pic.leaveStart,
    );
    if (invalidLeaveRange) {
      setSuccess(null);
      setError(`Leave end must be on or after leave start for ${invalidLeaveRange.name || 'the selected PIC'}.`);
      return;
    }

    const configToSave: FulfilmentPicConfig = {
      ...config,
      gigworkExcludedBrandSlugs: parsedBrandSlugs.slugs,
      gigworkDailyAccountOrderLimit: accountLimit,
      pics: config.pics.map((pic) => ({
        ...pic,
        weekendAssignments: parseWeekendAssignments(weekendDrafts[pic.picId] ?? pic.weekendAssignments.join(', '))
          .assignments,
      })),
    };

    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const latestConfig = await client.getConfig();
      const latestPolicy = normalizeConfig(latestConfig.config);
      const configToPersist: FulfilmentPicConfig = {
        ...configToSave,
        routingMode: latestPolicy.routingMode,
        gigworkDailyAccountOrderLimit:
          accountLimit !== loadedAccountPolicy.limit
            ? accountLimit
            : (latestPolicy.gigworkDailyAccountOrderLimit ?? 30),
        gigworkAccountLimitMode:
          configToSave.gigworkAccountLimitMode !== loadedAccountPolicy.mode
            ? (configToSave.gigworkAccountLimitMode ?? 'observe-only')
            : (latestPolicy.gigworkAccountLimitMode ?? 'observe-only'),
      };
      if (
        configToPersist.gigworkAccountLimitMode === 'enforce' &&
        latestConfig.config.gigworkAccountLimitMode !== 'enforce' &&
        !window.confirm(
          `Enable enforcement at ${configToPersist.gigworkDailyAccountOrderLimit} gig batch accept slots per Coupang account per KST day? ` +
            'New accepts will be blocked at the limit or when the account cannot be detected, including old extensions. ' +
            'Confirm workers have received the notice, the updated extension is rolled out, and logout/re-login detection has been verified. ' +
            'The worker daily limit remains 40. Prefer enabling at a KST day boundary.',
        )
      ) {
        return;
      }
      const result = await client.saveConfig(configToPersist);
      const savedConfig = normalizeConfig(result.config);
      setConfig(savedConfig);
      setAccountLimitDraft(String(savedConfig.gigworkDailyAccountOrderLimit));
      setLoadedAccountPolicy({
        limit: savedConfig.gigworkDailyAccountOrderLimit ?? 30,
        mode: savedConfig.gigworkAccountLimitMode ?? 'observe-only',
      });
      setExcludedBrandDraft(savedConfig.gigworkExcludedBrandSlugs.join(', '));
      setWeekendDrafts(createWeekendDrafts(savedConfig));
      setSuccess('PIC configuration saved.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save PIC config');
    } finally {
      setIsSaving(false);
    }
  };

  const coveragePics = config.pics.map((pic) => {
    const parsedWeekendAssignments = parseWeekendAssignments(
      weekendDrafts[pic.picId] ?? pic.weekendAssignments.join(', '),
    );
    return parsedWeekendAssignments.invalid.length === 0
      ? { ...pic, weekendAssignments: parsedWeekendAssignments.assignments }
      : pic;
  });
  const coverage = buildPicRosterCoverage(coveragePics, coverageAt);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" aria-label="Loading PIC configuration" />
      </div>
    );
  }

  return (
    <Container fluid className="py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2 className="mb-1">PIC Management</h2>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => void loadConfig()} disabled={isSaving}>
            Reload
          </Button>
          <Button onClick={() => void saveConfig()} disabled={isSaving || !hasLoadedConfig}>
            {isSaving ? 'Saving…' : 'Save configuration'}
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card className="mb-3">
        <Card.Header className="fw-semibold">GIGWORK allocation</Card.Header>
        <Card.Body>
          <Form.Group controlId="gigwork-allocation-percentage" style={{ maxWidth: 320 }}>
            <Form.Label>GIGWORK allocation percentage</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                aria-label="GIGWORK allocation percentage"
                type="number"
                min={0}
                max={100}
                step={1}
                value={config.gigworkAllocationPercentage}
                onChange={(event) =>
                  setConfig((current) => ({
                    ...current,
                    gigworkAllocationPercentage: Math.min(100, Math.max(0, Number(event.target.value) || 0)),
                  }))
                }
              />
              <span>%</span>
            </div>
          </Form.Group>
          <div className="text-muted small mt-2">
            Eligible orders are allocated to the shared OPEN pool up to the active workers&apos; combined daily limit.
          </div>
          <Form.Group controlId="gigwork-reward-krw" className="mt-3" style={{ maxWidth: 320 }}>
            <Form.Label>GIGWORK reward per accepted order</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <span>₩</span>
              <Form.Control
                aria-label="GIGWORK reward per accepted order"
                type="number"
                min={0}
                step={100}
                value={config.gigworkRewardKrw}
                onChange={(event) =>
                  setConfig((current) => ({
                    ...current,
                    gigworkRewardKrw: Math.max(0, Number(event.target.value) || 0),
                  }))
                }
              />
            </div>
          </Form.Group>
          <Form.Group controlId="gigwork-excluded-brand-slugs" className="mt-3" style={{ maxWidth: 640 }}>
            <Form.Label>Brands assigned to internal PICs</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              aria-label="Brands assigned to internal PICs"
              value={excludedBrandDraft}
              placeholder="beauty-of-joseon, medicube"
              onChange={(event) => setExcludedBrandDraft(event.target.value)}
            />
            <Form.Text muted>
              Enter canonical WooCommerce brand slugs separated by commas or new lines. Lines of these brands stay with
              an internal PIC; the order&apos;s other lines can still go to gig workers.
            </Form.Text>
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="fw-semibold">Coupang account daily limit</Card.Header>
        <Card.Body>
          <Form.Group controlId="gigwork-account-daily-limit" style={{ maxWidth: 320 }}>
            <Form.Label>Daily gig batch accept slots per Coupang account</Form.Label>
            <Form.Control
              type="number"
              min={1}
              step={1}
              value={accountLimitDraft}
              disabled={isSaving || !hasLoadedConfig}
              onChange={(event) => setAccountLimitDraft(event.target.value)}
            />
            <Form.Text muted>
              Per worker and Coupang account; resets at KST midnight. Counts batch accepts, not actual Coupang orders.
              The worker daily limit remains 40.
            </Form.Text>
          </Form.Group>
          <Form.Group controlId="gigwork-account-limit-mode" className="mt-3" style={{ maxWidth: 320 }}>
            <Form.Label>Coupang account limit mode</Form.Label>
            <Form.Select
              value={config.gigworkAccountLimitMode}
              disabled={isSaving || !hasLoadedConfig}
              onChange={(event) => {
                setConfig((current) => ({
                  ...current,
                  gigworkAccountLimitMode: event.target.value as GigworkAccountLimitMode,
                }));
              }}
            >
              <option value="observe-only">Observe only</option>
              <option value="enforce">Enforce</option>
            </Form.Select>
          </Form.Group>
          <div className="text-muted small mt-2">
            Observe only: counts detected accounts without blocking. Enforce: counts detected accounts and blocks new
            accepts at the account limit or without a detected account. Changes take effect only after saving. Notify
            workers and roll out the updated extension before enabling enforcement.
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="fw-semibold">Purpose coverage (today, SGT {coverage.dateSgt})</Card.Header>
        <Card.Body className="p-0">
          <Table responsive bordered size="sm" className="mb-0 align-middle" aria-label="Purpose coverage">
            <thead className="table-light">
              <tr>
                <th>Purpose</th>
                <th>Orders</th>
                <th>PICs</th>
                <th>Routing today</th>
              </tr>
            </thead>
            <tbody>
              {coverage.groups.map((group) => {
                const label = group.purpose ? picPurposeLabels[group.purpose] : 'No purpose';
                return (
                  <tr key={group.purpose ?? 'none'} aria-label={`${label} coverage`}>
                    <td className="fw-semibold text-nowrap">{label}</td>
                    <td className="small text-muted">
                      {group.purpose ? picPurposeDescriptions[group.purpose] : 'No purpose set.'}
                    </td>
                    <td>
                      {group.members.length === 0 ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <div className="d-flex flex-wrap gap-1">
                          {group.members.map(({ pic, status }) => (
                            <Badge
                              key={pic.picId}
                              bg={status === PicDutyStatus.ON_DUTY ? 'success' : 'secondary'}
                              className="fw-normal"
                            >
                              {pic.name.trim() || 'Unnamed PIC'} · {picDutyStatusLabels[status]}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="small">{describeRouting(group)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer className="small text-muted">
          In-stock lines are ignored. An order with both Coupang and non-Coupang lines left is weighed across the PICs
          of both purposes.
        </Card.Footer>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span className="fw-semibold">PIC roster ({config.pics.length})</span>
          <Button
            size="sm"
            onClick={() => setConfig((current) => ({ ...current, pics: [...current.pics, createPic()] }))}
          >
            Add PIC
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive bordered hover size="sm" className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Slack ID</th>
                <th>Active</th>
                <th style={{ minWidth: 190 }}>Purposes</th>
                <th style={{ minWidth: 260 }}>Working days</th>
                <th>Leave start</th>
                <th>Leave end</th>
                <th>Weight</th>
                <th style={{ minWidth: 190 }}>Weekend dates</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {config.pics.map((pic) => {
                const weekendDraft = weekendDrafts[pic.picId] ?? pic.weekendAssignments.join(', ');
                const parsedWeekendAssignments = parseWeekendAssignments(weekendDraft);
                return (
                  <tr key={pic.picId}>
                    <td>
                      <Form.Control
                        size="sm"
                        value={pic.name}
                        onChange={(event) => updatePic(pic.picId, { name: event.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={pic.slackId}
                        onChange={(event) => updatePic(pic.picId, { slackId: event.target.value })}
                      />
                    </td>
                    <td className="text-center">
                      <Form.Check
                        id={`${pic.picId}-active`}
                        aria-label={`${pic.name || 'PIC'} active`}
                        checked={pic.isActive}
                        onChange={(event) => updatePic(pic.picId, { isActive: event.target.checked })}
                      />
                    </td>
                    <td>
                      <div role="group" aria-label={`${pic.name || 'PIC'} purposes`}>
                        {purposes.map((purpose) => (
                          <Form.Check
                            key={purpose}
                            type="checkbox"
                            className="small mb-0"
                            id={`${pic.picId}-purpose-${purpose}`}
                            label={picPurposeLabels[purpose]}
                            checked={resolvePicPurposes(pic).includes(purpose)}
                            onChange={() => {
                              const nextPurposes = togglePurpose(resolvePicPurposes(pic), purpose);
                              updatePic(pic.picId, { purposes: nextPurposes, purpose: nextPurposes[0] ?? null });
                            }}
                          />
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        {weekdays.map((day) => (
                          <Form.Check
                            key={day}
                            inline
                            type="checkbox"
                            id={`${pic.picId}-${day}`}
                            label={day}
                            checked={pic.activeDays.includes(day)}
                            onChange={() => toggleActiveDay(pic, day)}
                          />
                        ))}
                      </div>
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="date"
                        value={pic.leaveStart ?? ''}
                        onChange={(event) => updatePic(pic.picId, { leaveStart: event.target.value || null })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="date"
                        value={pic.leaveEnd ?? ''}
                        onChange={(event) => updatePic(pic.picId, { leaveEnd: event.target.value || null })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={0}
                        step={1}
                        value={pic.weight}
                        onChange={(event) =>
                          updatePic(pic.picId, { weight: Math.max(0, Number(event.target.value) || 0) })
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        placeholder="YYYY-MM-DD, YYYY-MM-DD"
                        value={weekendDraft}
                        isInvalid={parsedWeekendAssignments.invalid.length > 0}
                        onChange={(event) =>
                          setWeekendDrafts((current) => ({ ...current, [pic.picId]: event.target.value }))
                        }
                        onBlur={() => {
                          if (parsedWeekendAssignments.invalid.length === 0) {
                            updatePic(pic.picId, { weekendAssignments: parsedWeekendAssignments.assignments });
                          }
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        Invalid: {parsedWeekendAssignments.invalid.join(', ')}
                      </Form.Control.Feedback>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() =>
                          setConfig((current) => ({
                            ...current,
                            pics: current.pics.filter((item) => item.picId !== pic.picId),
                          }))
                        }
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {config.pics.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-muted py-4">
                    No PICs configured.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
}
